/**
 * طلب وجبات
 * المستخدم يطلب لليوم التالي؛ التواريخ المتاحة وموعد الإقفال يأتيان من الخادم.
 *
 * إن كان للمديرية طلب مسجّل مسبقاً لنفس اليوم، تتحوّل الشاشة تلقائياً
 * لوضع التعديل (تحميل الكميات القائمة، PUT بدل POST) طالما لم يفت
 * موعد الإقفال أو يُقفل الطلب يدوياً.
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import { useActiveItems, groupByCategory } from "../../hooks/useItems";
import { ITEM_UNIT_LABELS } from "../../types";
import type { Governorate, Directorate, Restaurant, MealEntry } from "../../types";

interface OpenDate { date: string; allowed: boolean; reason?: string; deadline: string }
interface OpenDates {
  settings: { cutoffTime: string; leadDays: number; timezone: string };
  dates: OpenDate[];
}

export default function MealEntryPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const lockedDirectorateId = user?.directorateId ?? null;
  const topRef = useRef<HTMLDivElement>(null);

  const { data: items = [], isLoading: itemsLoading } = useActiveItems();

  // التواريخ المتاحة للطلب — الخادم هو المرجع، لا حساب في الواجهة
  const { data: open, isLoading: datesLoading } = useQuery({
    queryKey: ["open-dates"],
    staleTime: 60_000,
    queryFn: () => api.get<{ data: OpenDates }>("/meal-entries/open-dates").then((r) => r.data.data),
  });

  const selectable = useMemo(() => open?.dates.filter((d) => d.allowed) ?? [], [open]);
  const blocked    = useMemo(() => open?.dates.filter((d) => !d.allowed) ?? [], [open]);

  const [entryDate,      setEntryDate]      = useState("");
  const [governorateId,  setGovernorateId]  = useState("");
  const [directorateId,  setDirectorateId]  = useState("");
  const [restaurantId,   setRestaurantId]   = useState("");
  const [notes,          setNotes]          = useState("");
  const [qty,            setQty]            = useState<Record<number, string>>({});

  const [governorates, setGovernorates] = useState<Governorate[]>([]);
  const [directorates, setDirectorates] = useState<Directorate[]>([]);
  const [restaurants,  setRestaurants]  = useState<Restaurant[]>([]);
  const [success, setSuccess] = useState("");
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);

  const groups = useMemo(() => groupByCategory(items), [items]);
  const chosen = open?.dates.find((d) => d.date === entryDate);

  // هل يوجد طلب مسجَّل مسبقاً لهذا (اليوم × المديرية)؟ إن وُجد ⇒ وضع تعديل
  const dirIdNum = directorateId ? parseInt(directorateId, 10) : null;
  const { data: existingList } = useQuery({
    queryKey: ["meal-entries", "existing", entryDate, dirIdNum],
    enabled: !!entryDate && !!dirIdNum,
    queryFn: () =>
      api.get<{ data: MealEntry[] }>(`/meal-entries?from=${entryDate}&to=${entryDate}&directorateId=${dirIdNum}`)
         .then((r) => r.data.data),
  });
  const existing = existingList?.[0] ?? null;
  const isEditing = !!existing;
  const isReceived = !!existing?.receivedAt;
  const isLocked  = !!existing?.lockedAt || isReceived;

  // تحميل الطلب القائم إلى النموذج عند العثور عليه
  useEffect(() => {
    if (!existing) return;
    const q: Record<number, string> = {};
    for (const l of existing.lines) q[l.itemId] = String(l.qty);
    setQty(q);
    setNotes(existing.notes ?? "");
    setRestaurantId(existing.restaurant?.id ? String(existing.restaurant.id) : "");
  }, [existing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // افتراضياً: أول تاريخ متاح (الغد عادةً)
  useEffect(() => {
    if (!entryDate && selectable.length > 0) setEntryDate(selectable[0]!.date);
  }, [selectable, entryDate]);

  useEffect(() => {
    api.get<{ data: Governorate[] }>("/governorates?isActive=true")
      .then((r) => setGovernorates(r.data.data)).catch(() => setError("تعذّر تحميل المحافظات"));
    api.get<{ data: Directorate[] }>("/directorates?isActive=true")
      .then((r) => setDirectorates(r.data.data)).catch(() => setError("تعذّر تحميل المديريات"));
  }, []);

  // المستخدم المقيّد: المديرية ثابتة، ومحافظتها تُعرف منها
  useEffect(() => {
    if (lockedDirectorateId == null) return;
    setDirectorateId(String(lockedDirectorateId));
    const d = directorates.find((x) => x.id === lockedDirectorateId);
    if (d) setGovernorateId(String(d.governorateId));
  }, [lockedDirectorateId, directorates]);

  // المديريات المتاحة = مديريات المحافظة المختارة فقط
  const dirOptions = lockedDirectorateId != null
    ? directorates.filter((d) => d.id === lockedDirectorateId)
    : directorates.filter((d) => governorateId && d.governorateId === Number(governorateId));

  useEffect(() => {
    if (!governorateId) { setRestaurants([]); return; }
    api.get<{ data: Restaurant[] }>(`/restaurants?governorateId=${governorateId}&isActive=true`)
      .then((r) => setRestaurants(r.data.data)).catch(() => setRestaurants([]));
  }, [governorateId]);

  const reset = () => {
    setQty({}); setNotes(""); setRestaurantId("");
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(""); setSuccess("");

    if (!entryDate)                    { setError("اختر تاريخ التنفيذ"); return; }
    if (!governorateId || !directorateId) { setError("المحافظة والمديرية مطلوبان"); return; }

    const lines = items
      .map((it) => ({ itemId: it.id, qty: parseFloat(qty[it.id] ?? "") }))
      .filter((l) => Number.isFinite(l.qty) && l.qty > 0);

    if (lines.length === 0) { setError("أدخل كمية واحدة أكبر من صفر على الأقل"); return; }

    const bad = lines.find((l) => {
      const item = items.find((i) => i.id === l.itemId);
      if (!item) return false;
      const f = 10 ** item.maxDecimalPlaces;
      return Math.round(l.qty * f) / f !== l.qty;
    });
    if (bad) {
      const item = items.find((i) => i.id === bad.itemId)!;
      setError(
        item.maxDecimalPlaces === 0
          ? `الصنف "${item.nameAr}" لا يقبل كسوراً عشرية`
          : `الصنف "${item.nameAr}" يقبل ${item.maxDecimalPlaces} منزلة عشرية على الأكثر`
      );
      return;
    }

    setLoading(true);
    try {
      const payload = {
        restaurantId: restaurantId ? parseInt(restaurantId, 10) : null,
        notes:        notes || null,
        lines,
      };

      const res = existing
        ? await api.put(`/meal-entries/${existing.id}`, payload)
        : await api.post("/meal-entries", {
            ...payload, entryDate,
            governorateId: parseInt(governorateId, 10),
            directorateId: parseInt(directorateId, 10),
          });

      setSuccess((res.data as { message?: string }).message ?? "تم الحفظ بنجاح ✓");
      reset();
      await qc.invalidateQueries({ queryKey: ["meal-entries"] });
      // الانتقال لأعلى الصفحة ليلاحظ المستخدم رسالة النجاح فوراً
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "حدث خطأ أثناء الإرسال"
      );
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = !!chosen?.allowed && !isLocked;

  return (
    <div className="max-w-3xl mx-auto" ref={topRef}>
      <div className="page-header">
        <h1 className="page-title">{isEditing ? "تعديل طلب الوجبات" : "طلب وجبات"}</h1>
        <p className="page-subtitle">
          تُرسَل الطلبات مقدماً ليوم التنفيذ
          {open && ` — موعد الإقفال ${open.settings.cutoffTime}`}
        </p>
      </div>

      {success && <div className="alert-success mb-4">{success}</div>}
      {error   && <div className="alert-error   mb-4">{error}</div>}

      {isEditing && !isLocked && canSubmit && (
        <div className="alert-info mb-4">
          يوجد طلب مُرسَل مسبقاً لهذا اليوم لنفس المديرية — أنت الآن تعدّله، لا تُنشئ طلباً جديداً.
        </div>
      )}
      {isEditing && isLocked && (
        <div className="alert-error mb-4">
          {isReceived
            ? "تم تأكيد استلام هذا الطلب — مقفل نهائياً ولا يمكن تعديله."
            : "هذا الطلب مقفل ولا يمكن تعديله — تجاوز موعد الإقفال بعد إرساله. لتصحيح خطأ راجع مدير النظام (كل الطلبات ← تعديل)."}
        </div>
      )}
      {isEditing && !isLocked && !canSubmit && chosen && (
        <div className="alert-error mb-4">{chosen.reason}</div>
      )}

      {!datesLoading && selectable.length === 0 && (
        <div className="alert-error mb-4">
          لا توجد تواريخ متاحة للطلب حالياً — انتهى موعد الإقفال لكل الأيام القادمة.
          {blocked[0]?.reason && <div className="text-xs mt-1">{blocked[0].reason}</div>}
        </div>
      )}

      <form onSubmit={submit} className="card space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="label" htmlFor="entryDate">يوم التنفيذ</label>
            <select id="entryDate" className="input" required
              value={entryDate} onChange={(e) => setEntryDate(e.target.value)}>
              <option value="">-- اختر اليوم --</option>
              {open?.dates.map((d) => (
                <option key={d.date} value={d.date} disabled={!d.allowed}>
                  {d.date}{d.allowed ? "" : " (مغلق)"}
                </option>
              ))}
            </select>
            {chosen?.allowed && (
              <p className="text-[11px] text-sand-500 mt-1">يُقبل حتى {chosen.deadline}</p>
            )}
            {chosen && !chosen.allowed && (
              <p className="text-[11px] text-red-600 mt-1">{chosen.reason}</p>
            )}
          </div>
          <div>
            <label className="label" htmlFor="gov">المحافظة</label>
            <select id="gov" className="input" required disabled={lockedDirectorateId != null}
              value={governorateId}
              onChange={(e) => { setGovernorateId(e.target.value); setDirectorateId(""); }}>
              <option value="">-- اختر المحافظة --</option>
              {governorates.map((g) => <option key={g.id} value={g.id}>{g.nameAr}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="dir">المديرية / الفرع</label>
            <select id="dir" className="input" required
              disabled={lockedDirectorateId != null || !governorateId}
              value={directorateId} onChange={(e) => setDirectorateId(e.target.value)}>
              <option value="">{governorateId ? "-- اختر المديرية --" : "اختر المحافظة أولاً"}</option>
              {dirOptions.map((d) => <option key={d.id} value={d.id}>{d.nameAr}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="label" htmlFor="rest">المطعم</label>
          <select id="rest" className="input" disabled={!governorateId || isLocked}
            value={restaurantId} onChange={(e) => setRestaurantId(e.target.value)}>
            <option value="">
              {governorateId
                ? (restaurants.length ? "-- بدون مطعم محدد --" : "لا توجد مطاعم مسجّلة لهذه المحافظة")
                : "اختر المحافظة أولاً"}
            </option>
            {restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          {governorateId && restaurants.length === 0 && (
            <p className="text-[11px] text-sand-500 mt-1">
              راجع مدير النظام لإضافة مطاعم هذه المحافظة من صفحة القواميس.
            </p>
          )}
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-700">الكميات المطلوبة</h3>
          {itemsLoading && <p className="text-sm text-sand-400">جاري تحميل الأصناف...</p>}
          {!itemsLoading && groups.length === 0 && (
            <div className="alert-error">
              لا توجد أصناف مفعّلة — راجع مدير النظام لإضافتها من صفحة القواميس.
            </div>
          )}
          {groups.map((group) => (
            <fieldset key={group.id} className="border border-sand-300 rounded-lg p-3">
              <legend className="px-2 text-xs font-semibold text-sand-600">{group.nameAr}</legend>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {group.items.map((item) => (
                  <div key={item.id}>
                    <label className="label" htmlFor={`item-${item.id}`}>
                      {item.nameAr}
                      <span className="text-sand-400 text-xs mr-1">({ITEM_UNIT_LABELS[item.unit]})</span>
                    </label>
                    <input id={`item-${item.id}`} type="number" inputMode="decimal" disabled={isLocked}
                      min="0" step={item.maxDecimalPlaces > 0 ? String(1 / 10 ** item.maxDecimalPlaces) : "1"}
                      className="input" placeholder="0"
                      value={qty[item.id] ?? ""}
                      onChange={(e) => setQty((q) => ({ ...q, [item.id]: e.target.value }))} />
                  </div>
                ))}
              </div>
            </fieldset>
          ))}
        </div>

        <div>
          <label className="label" htmlFor="notes">ملاحظات</label>
          <textarea id="notes" rows={3} className="input resize-none" disabled={isLocked}
            value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="ملاحظات اختيارية..." />
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={reset} disabled={isLocked}>مسح النموذج</button>
          <button type="submit" className="btn-primary"
            disabled={loading || itemsLoading || !canSubmit}>
            {loading ? "جاري الحفظ..." : isEditing ? "حفظ التعديل" : "إرسال الطلب"}
          </button>
        </div>
      </form>
    </div>
  );
}
